import React, { useState } from 'react';
import { useConfigStore } from '@stores/configStore';
import { Lock, Plus, Trash2 } from 'lucide-react';
import type { SecurityConfig } from '../types/config';

interface SecurityConfigFormProps {}

export const SecurityConfigForm: React.FC<SecurityConfigFormProps> = () => {
  const { config, updateSecurity } = useConfigStore();
  const security = config.security;
  const [showJWTSecret, setShowJWTSecret] = useState(false);
  const [newAPIKeyName, setNewAPIKeyName] = useState('');

  const handleUpdate = (updates: Partial<SecurityConfig>) => {
    updateSecurity(updates);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold">Security Configuration</h3>
      </div>

      {/* JWT Configuration */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">JWT Configuration</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">JWT Secret</label>
            <div className="relative">
              <input
                type={showJWTSecret ? 'text' : 'password'}
                value={security.jwtSecret}
                onChange={(e) => handleUpdate({ jwtSecret: e.target.value })}
                placeholder="••••••••••••••••"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowJWTSecret(!showJWTSecret)}
                className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
              >
                {showJWTSecret ? '🙈' : '👁️'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Secret key for JWT token signing (min 32 characters recommended)</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">JWT Expiration (hours)</label>
              <input
                type="number"
                value={security.jwtExpiration}
                onChange={(e) => handleUpdate({ jwtExpiration: parseInt(e.target.value) })}
                min="1"
                max="720"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Token expiration time in hours</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Login Attempts</label>
              <input
                type="number"
                value={security.maxLoginAttempts}
                onChange={(e) => handleUpdate({ maxLoginAttempts: parseInt(e.target.value) })}
                min="1"
                max="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Maximum failed login attempts before lockout</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lockout Duration (minutes)</label>
            <input
              type="number"
              value={security.lockoutDuration}
              onChange={(e) => handleUpdate({ lockoutDuration: parseInt(e.target.value) })}
              min="1"
              max="1440"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Account lockout duration after max attempts</p>
          </div>
        </div>
      </div>

      {/* Password Policy */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Password Policy</h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Password Length</label>
            <input
              type="number"
              value={security.passwordMinLength}
              onChange={(e) => handleUpdate({ passwordMinLength: parseInt(e.target.value) })}
              min="6"
              max="32"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Minimum number of characters required</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requireUppercase"
                checked={security.passwordRequireUppercase}
                onChange={(e) => handleUpdate({ passwordRequireUppercase: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="requireUppercase" className="text-sm font-medium text-gray-700">
                Require Uppercase Letters (A-Z)
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requireLowercase"
                checked={security.passwordRequireLowercase}
                onChange={(e) => handleUpdate({ passwordRequireLowercase: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="requireLowercase" className="text-sm font-medium text-gray-700">
                Require Lowercase Letters (a-z)
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requireNumbers"
                checked={security.passwordRequireNumbers}
                onChange={(e) => handleUpdate({ passwordRequireNumbers: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="requireNumbers" className="text-sm font-medium text-gray-700">
                Require Numbers (0-9)
              </label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requireSpecialChars"
                checked={security.passwordRequireSpecialChars}
                onChange={(e) => handleUpdate({ passwordRequireSpecialChars: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="requireSpecialChars" className="text-sm font-medium text-gray-700">
                Require Special Characters (!@#$%^&*)
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* MFA Settings */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">Multi-Factor Authentication</h4>
        
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="enableMFA"
            checked={security.enableMFA}
            onChange={(e) => handleUpdate({ enableMFA: e.target.checked })}
            className="w-4 h-4 text-blue-600 rounded"
          />
          <label htmlFor="enableMFA" className="text-sm font-medium text-gray-700">
            Enable Multi-Factor Authentication (MFA)
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">Require users to provide additional verification during login</p>
      </div>

      {/* API Key Management */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">API Key Management</h4>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Manage API keys for external integrations</p>
          
          <div className="bg-gray-50 rounded-lg p-4 space-y-3 max-h-64 overflow-y-auto">
            {/* API Keys would be displayed here */}
            <div className="text-sm text-gray-500 text-center py-4">
              No API keys configured yet
            </div>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={newAPIKeyName}
              onChange={(e) => setNewAPIKeyName(e.target.value)}
              placeholder="Enter API key name"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={() => {
                if (newAPIKeyName.trim()) {
                  // API key creation would be handled here
                  setNewAPIKeyName('');
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Key
            </button>
          </div>
        </div>
      </div>

      {/* SSL/TLS Configuration */}
      <div className="border-b pb-4">
        <h4 className="font-semibold text-gray-900 mb-4">SSL/TLS Configuration</h4>
        
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              id="sslEnabled"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="sslEnabled" className="text-sm font-medium text-gray-700">
              Enforce SSL/TLS for All Connections
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Path</label>
            <input
              type="text"
              placeholder="/path/to/certificate.pem"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Path to SSL certificate file</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Private Key Path</label>
            <input
              type="text"
              placeholder="/path/to/private-key.pem"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">Path to SSL private key file</p>
          </div>
        </div>
      </div>

      {/* Audit Logging */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-4">Audit Logging</h4>
        
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="auditLogging"
              defaultChecked={true}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="auditLogging" className="text-sm font-medium text-gray-700">
              Enable Audit Logging
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Log Level</label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="debug">Debug</option>
              <option value="info">Info</option>
              <option value="warn">Warning</option>
              <option value="error">Error</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Minimum log level for audit events</p>
          </div>

          <p className="text-xs text-gray-500">
            Audit logs track all user actions, configuration changes, and security events for compliance and troubleshooting
          </p>
        </div>
      </div>
    </div>
  );
};
